import { useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useAdminOrgStore } from "@/stores/admin-org";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export type WasteFoodOption = { id: string; label: string };

/**
 * Distinct identified foods present in the org's waste events — the option
 * source for the FilterPopover "Food" filter (POS2-758). Bounded to items that
 * actually appear in waste, not the full catalogue.
 *
 * `enabled` gates the fetch (POS2-960): the underlying RPC scans the org's whole
 * waste history, so pages defer it until the filter popover is first opened
 * rather than paying it on every cold page load. Failure is non-blocking — the
 * consumer falls back to an empty option list.
 */
export function useWasteFoods(enabled = true) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const orgId = useAdminOrgStore((s) => s.orgId);
  return useQuery<WasteFoodOption[]>({
    queryKey: ["waste-foods", orgId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/waste/events/foods`, { headers });
      if (!res.ok) throw await apiError(t, res);
      const body = (await res.json()) as { data: WasteFoodOption[] };
      return body.data;
    },
    enabled: enabled && !!headers.Authorization.replace("Bearer ", ""),
    staleTime: 5 * 60_000,
  });
}
